import { Box, Button, useMediaQuery } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';

import loginBackground2 from '../../assets/images/login-background-2.png';

const AuthLayout = ({ children, onBack, backgroundImage }) => {
  const isMobileOrTablet = useMediaQuery('(max-width:960px)'); // Combined mobile/tablet check
  const isDesktop = useMediaQuery('(min-width:961px)'); // Desktop only

  // Use the provided background image or default to loginBackground
  const bgImage = backgroundImage || loginBackground2;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        overflow: { xs: 'auto', md: 'hidden' },
        position: 'relative',
        // Apply background for mobile AND tablet (up to 960px)
        ...(isMobileOrTablet && {
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.65)',
            zIndex: 1
          }
        })
      }}
    >
      {/* Back Button */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 16, md: 20 },
          left: { xs: 12, sm: 16, md: 20 },
          zIndex: 1000
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            color: isMobileOrTablet ? 'white' : '#666',
            backgroundColor: isMobileOrTablet
              ? 'rgba(0, 0, 0, 0.5)'
              : 'transparent',
            backdropFilter: isMobileOrTablet ? 'blur(10px)' : 'none',
            '&:hover': {
              backgroundColor: isMobileOrTablet
                ? 'rgba(0, 0, 0, 0.7)'
                : 'rgba(0, 0, 0, 0.04)'
            },
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: 2,
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 0.75, sm: 1, md: 1.25 },
            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
            minHeight: { xs: '36px', sm: '40px', md: '44px' }
          }}
        >
          Back
        </Button>
      </Box>

      {/* Form Panel */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          width: isDesktop ? '45%' : '100%',
          display: 'flex',
          zIndex: 2,
          position: 'relative'
        }}
      >
        <Box
          sx={{
            width: '100%',
            minHeight: '100vh',
            height: 'auto',
            overflow: 'auto',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            // Mobile and tablet responsive container - use card layout
            ...(isMobileOrTablet && {
              backgroundColor: 'transparent', // Make background transparent for mobile/tablet
              padding: {
                xs: '64px 12px 24px 12px',
                sm: '72px 16px 32px 16px'
              },
              alignItems: 'center',
              justifyContent: 'flex-start'
            }),
            // Desktop only
            ...(isDesktop && {
              padding: {
                md: '80px 28px 40px 28px',
                lg: '80px 40px 40px 40px'
              },
              justifyContent: 'center',
              boxShadow: '0 0 24px rgba(0,0,0,0.12)'
            })
          }}
        >
          {/* Mobile and Tablet Card Wrapper */}
          {isMobileOrTablet ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '360px', sm: '420px' },
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                borderRadius: { xs: 2.5, sm: 3 },
                padding: { xs: '20px', sm: '28px' },
                boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                position: 'relative',
                margin: 'auto 0'
              }}
            >
              {children}
            </Box>
          ) : (
            /* Desktop Direct Content */
            <Box
              sx={{
                width: '100%',
                maxWidth: { md: '500px', lg: '540px' },
                margin: '0 auto'
              }}
            >
              {children}
            </Box>
          )}
        </Box>
      </motion.div>

      {/* Right Panel - Background Image (desktop only) */}
      {isDesktop && (
        <Box
          sx={{
            flex: 1,
            minHeight: '100vh',
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.15)',
              zIndex: 1
            }
          }}
        />
      )}
    </Box>
  );
};

export default AuthLayout;
